<!-- ModalDetallePedido.vue -->
<template>
  <div class="modal fade" id="modalDetalle" tabindex="-1" ref="el">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content modal-divisan border-0">
        <!-- Header -->
        <div class="modal-header border-0 pb-2">
          <div class="header-info">
            <div class="header-cliente">
              <span class="badge-cliente">{{ pedido?.cliente_numero }}</span>
              <span class="cliente-nombre">{{ pedido?.cliente }}</span>
            </div>
            <h5 class="header-titulo">Pedido #{{ pedido?.numero_pedido }}</h5>
          </div>
          <button
            type="button"
            class="btn-close flex-shrink-0 ms-2"
            data-bs-dismiss="modal"
          ></button>
        </div>

        <div class="modal-body pt-0">
          <!-- Resumen -->
          <div class="resumen-row">
            <div class="resumen-item">
              <span class="resumen-label">Recibo</span>
              <span class="resumen-val">{{ formatFecha(pedido?.fecha_recibo) }}</span>
            </div>
            <div class="resumen-item">
              <span class="resumen-label">Compromiso</span>
              <span class="resumen-val">{{ formatFecha(pedido?.fecha_compromiso) }}</span>
            </div>
            <div class="resumen-item">
              <span class="resumen-label">Entrega real</span>
              <span class="resumen-val">{{
                formatFecha(pedido?.fecha_entrega_real)
              }}</span>
            </div>
            <div class="resumen-item">
              <span class="resumen-label">Días restantes</span>
              <span :class="badgeDias(pedido?.dias_restantes)">{{
                labelDias(pedido?.dias_restantes)
              }}</span>
            </div>
            <div class="resumen-item">
              <span class="resumen-label">Prioridad</span>
              <span class="resumen-val">{{ pedido?.prioridad }}</span>
            </div>
            <div class="resumen-item">
              <span class="resumen-label">Total piezas</span>
              <span class="resumen-val text-verde fw-bold">
                {{ Number(pedido?.total || 0).toLocaleString("es-MX") }}
              </span>
            </div>
          </div>

          <div class="notas-guia-row">
            <!-- Notas -->
            <div :class="['notas-pedido', { 'notas-vacio': !pedido?.notas }]">
              <span class="notas-label">
                <i class="bi bi-chat-left-text me-1"></i>Notas
              </span>
              <p class="notas-texto">{{ pedido?.notas || "Sin notas registradas" }}</p>
            </div>

            <!-- Guías de envío -->
            <div :class="['notas-pedido', { 'notas-vacio': !pedido?.guias?.length }]">
              <span class="notas-label">
                <i class="bi bi-truck me-1"></i>Guía(s) de envío
              </span>
              <p v-if="!pedido?.guias?.length" class="notas-texto">Sin guía registrada</p>
              <div v-else class="guias-lista">
                <div v-for="guia in pedido.guias" :key="guia.id" class="guia-chip">
                  <span class="guia-paqueteria">{{ guia.paqueteria_nombre }}</span>
                  <span class="guia-numero">{{ guia.numero_guia }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="divider"></div>

          <!-- Tabla ítems -->
          <div class="tabla-meta" v-if="!cargando && items.length > 0">
            <span>{{ items.length }} artículo(s)</span>
            <span class="tabla-meta-sep">·</span>
            <span>
              Total:
              <strong class="text-verde">{{ totalItems.toLocaleString("es-MX") }}</strong>
              pzas
            </span>
          </div>

          <div class="items-wrapper">
            <!-- Cargando -->
            <div v-if="cargando" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-verde me-2"></div>
              <span class="text-cafe small fw-bold">Cargando ítems...</span>
            </div>

            <!-- Sin ítems -->
            <div
              v-else-if="items.length === 0"
              class="text-center py-4 text-cafe opacity-50 small"
            >
              Sin ítems
            </div>

            <!-- Lista de ítems tipo card-row -->
            <template v-else>
              <div
                v-for="(item, idx) in items"
                :key="item.clave"
                :class="['item-row', { 'item-row--border': idx > 0 }]"
              >
                <!-- Línea 1: badge código + cantidad -->
                <div class="item-row-header">
                  <span>
                    <span class="badge-molde">{{ item.codigo }}</span>
                    <span v-if="item.tipo === 'servicio'" class="badge-servicio ms-1">Servicio</span>
                  </span>
                  <span class="item-cantidad">
                    {{ item.cantidad.toLocaleString("es-MX") }}
                    {{ item.tipo === "servicio" ? "" : "pzs" }}
                  </span>
                </div>
                <!-- Línea 2: notas / series (solo si existen) -->
                <p v-if="item.notas" class="item-notas">{{ item.notas }}</p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { Modal } from "bootstrap";
import { usePedidoBadges } from "./composables/usePedidoBadges.js";
import { getPedidoItems } from "@/utils/api_backend";

const pedido = ref(null);
const items = ref([]);
const cargando = ref(false);
const el = ref(null);
let modalInstance = null;

onMounted(() => {
  modalInstance = new Modal(el.value);
});

async function abrir(p) {
  pedido.value = p;
  items.value = [];
  modalInstance.show();
  cargando.value = true;
  try {
    items.value = await getPedidoItems(p.numero_pedido);
  } catch (e) {
    console.error("Error cargando ítems:", e);
  } finally {
    cargando.value = false;
  }
}
defineExpose({ abrir });

// Los servicios (envío, impresiones extra) no son piezas.
const totalItems = computed(() =>
  items.value
    .filter((i) => i.tipo !== "servicio")
    .reduce((s, i) => s + (Number(i.cantidad) || 0), 0)
);

const { formatFecha, labelDias, badgeDias } = usePedidoBadges();
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}

.text-verde {
  color: var(--d-green);
}

/* ── Modal ──────────────────────────────────────────────── */
.modal-divisan {
  background-color: var(--d-cream);
  border: 1.5px solid rgba(64, 34, 8, 0.2);
  border-radius: 18px;
}

/* ── Header ─────────────────────────────────────────────── */
.modal-header {
  border-bottom: 0.5px solid rgba(64, 34, 8, 0.12) !important;
  padding: 1.1rem 1.4rem 0.9rem;
  align-items: flex-start; /* btn-close alineado arriba cuando nombre es largo */
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0; /* permite que el flex-child se encoja */
  flex: 1;
}

.header-cliente {
  display: flex;
  align-items: baseline;
  gap: 7px;
  flex-wrap: wrap; /* nombre largo baja a segunda línea */
  margin-bottom: 1px;
  min-width: 0;
}

.badge-cliente {
  background: var(--d-green-dark);
  color: var(--d-cream);
  font-family: var(--d-font-mono);
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 4px;
  padding: 1px 7px;
  flex-shrink: 0; /* el badge nunca se encoge */
}

.cliente-nombre {
  font-size: 0.82rem;
  color: var(--d-green);
  font-weight: 600;
  word-break: break-word; /* nombre muy largo rompe línea */
  overflow-wrap: break-word;
  min-width: 0;
}

.header-titulo {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--d-brown);
  margin: 0;
}

/* ── Resumen ─────────────────────────────────────────────── */
.resumen-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr); /* 3 columnas en desktop */
  gap: 0;
  padding: 0.75rem 0;
  border-bottom: 0.5px solid rgba(64, 34, 8, 0.1);
  margin-bottom: 1rem;
}

.resumen-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0.45rem 0.5rem;
  border-bottom: 0.5px solid rgba(64, 34, 8, 0.06);
}

/* Quitar borde inferior de la última fila (items 5 y 6 en grid de 3) */
.resumen-item:nth-last-child(-n + 3) {
  border-bottom: none;
}

.resumen-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--d-green);
  letter-spacing: 0.03em;
}

.resumen-val {
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--d-brown);
}

/* ── Notas + guías ──────────────────────────────────────── */
.notas-guia-row {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.notas-pedido {
  flex: 1;
  background: white;
  border: 0.5px solid rgba(96, 108, 56, 0.25);
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
}

.notas-vacio {
  opacity: 0.5;
}

.notas-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--d-green);
  display: block;
  margin-bottom: 3px;
}

.notas-texto {
  font-size: 0.85rem;
  color: var(--d-brown);
  margin: 0;
  word-break: break-word;
}

/* ── Guías de envío ─────────────────────────────────────── */
.guias-lista {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 2px;
}

.guia-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.guia-paqueteria {
  background: var(--d-green-dark);
  color: var(--d-cream);
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 4px;
  padding: 1px 7px;
  white-space: nowrap;
  flex-shrink: 0;
}

.guia-numero {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--d-brown);
  font-family: var(--d-font-mono);
}

/* ── Divisor ─────────────────────────────────────────────── */
.divider {
  border-top: 0.5px solid rgba(64, 34, 8, 0.1);
  margin-bottom: 0.75rem;
}

/* ── Meta tabla ─────────────────────────────────────────── */
.tabla-meta {
  font-size: 0.8rem;
  color: rgba(64, 34, 8, 0.55);
  margin-bottom: 8px;
  display: flex;
  gap: 6px;
  align-items: center;
}

.tabla-meta-sep {
  opacity: 0.4;
}

/* ── Ítems card-row ─────────────────────────────────────── */
.items-wrapper {
  border-radius: 10px;
  border: 0.5px solid rgba(96, 108, 56, 0.2);
  overflow: hidden;
  background: white;
}

.item-row {
  padding: 0.55rem 0.85rem;
}

.item-row--border {
  border-top: 0.5px solid rgba(96, 108, 56, 0.12);
}

.item-row:hover {
  background: rgba(96, 108, 56, 0.03);
}

.item-row-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.item-cantidad {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
  flex-shrink: 0;
}

.item-notas {
  margin: 3px 0 0 0;
  font-size: 0.78rem;
  color: rgba(64, 34, 8, 0.6);
  line-height: 1.4;
  word-break: break-word;
}

/* ── Badges ─────────────────────────────────────────────── */
.badge-molde {
  color: var(--d-brown);
  border-radius: 6px;
  font-family: var(--d-font-mono);
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  display: inline-block;
}
.badge-servicio {
  background: rgba(21, 101, 192, 0.12);
  color: #1565c0;
  border-radius: 20px;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 8px;
  vertical-align: middle;
}

/* ── Responsive móvil ───────────────────────────────────── */
@media (max-width: 576px) {
  .modal-header {
    padding: 0.9rem 1rem 0.7rem;
  }

  /* Header: título de pedido más compacto */
  .header-titulo {
    font-size: 0.95rem;
  }

  /* Resumen: 2 columnas en móvil (antes 3) */
  .resumen-row {
    grid-template-columns: repeat(2, 1fr);
  }

  /* Reajustar borde inferior: en grid de 2 los últimos 2 no llevan borde */
  .resumen-item:nth-last-child(-n + 3) {
    border-bottom: 0.5px solid rgba(64, 34, 8, 0.06);
  }
  .resumen-item:nth-last-child(-n + 2) {
    border-bottom: none;
  }

  .resumen-label {
    font-size: 0.65rem;
  }

  .resumen-val {
    font-size: 0.82rem;
  }

  /* Notas + guías: apiladas verticalmente */
  .notas-guia-row {
    flex-direction: column;
    gap: 0.6rem;
  }

  /* Ítems: padding más compacto en móvil */
  .item-row {
    padding: 0.45rem 0.7rem;
  }

  .item-cantidad {
    font-size: 0.8rem;
  }

  .item-notas {
    font-size: 0.73rem;
  }
}

@media (max-width: 991.98px) {
  :deep(.modal-dialog) {
    margin-top: 5rem;
  }

  :deep(.modal-content) {
    max-height: 80vh;
  }
}
</style>
